import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { Branch } from '../../types';

const validationSchema = Yup.object({
  name: Yup.string().required('El nombre es requerido'),
  address: Yup.string(),
  phone: Yup.string(),
  email: Yup.string().email('Email inválido'),
  commissionRate: Yup.number()
    .min(0, 'Debe ser mayor o igual a 0')
    .max(100, 'Debe ser menor o igual a 100')
});

const BranchManagement: React.FC = () => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/branches');
      setBranches(response.data);
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (branch?: Branch) => {
    setSelectedBranch(branch || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBranch(null);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (selectedBranch) {
        await axiosInstance.put(`/branches/${selectedBranch.id}`, values);
        setSuccess('Sucursal actualizada correctamente');
      } else {
        await axiosInstance.post('/branches', values);
        setSuccess('Sucursal creada correctamente');
      }
      fetchBranches();
      handleCloseDialog();
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Error al guardar la sucursal');
    }
  };

  const handleDelete = async (branchId: string) => {
    if (window.confirm('¿Estás seguro de que deseas desactivar esta sucursal?')) {
      try {
        await axiosInstance.delete(`/branches/${branchId}`);
        fetchBranches();
        setSuccess('Sucursal desactivada correctamente');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error: any) {
        setError(error.message || 'Error al desactivar la sucursal');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Sucursales</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Sucursal
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Dirección</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Comisión (%)</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Usuarios</TableCell>
              <TableCell>Empresas</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.map((branch) => (
              <TableRow key={branch.id}>
                <TableCell>
                  <Typography fontWeight={500}>{branch.name}</Typography>
                </TableCell>
                <TableCell>{branch.address || '-'}</TableCell>
                <TableCell>{branch.phone || '-'}</TableCell>
                <TableCell>{branch.email || '-'}</TableCell>
                <TableCell>{branch.commissionRate}%</TableCell>
                <TableCell>
                  <Chip
                    label={branch.isActive ? 'Activa' : 'Inactiva'}
                    color={branch.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{branch._count?.users ?? 0}</TableCell>
                <TableCell>{branch._count?.companies ?? 0}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(branch)} size="small">
                    <EditIcon />
                  </IconButton>
                  {branch.isActive && (
                    <IconButton onClick={() => handleDelete(branch.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}
        </DialogTitle>
        <Formik
          initialValues={{
            name: selectedBranch?.name || '',
            address: selectedBranch?.address || '',
            phone: selectedBranch?.phone || '',
            email: selectedBranch?.email || '',
            commissionRate: selectedBranch?.commissionRate ?? 0,
            isActive: selectedBranch?.isActive ?? true
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="name"
                  label="Nombre"
                  autoComplete="off"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="address"
                  label="Dirección"
                  autoComplete="off"
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address && errors.address}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="phone"
                  label="Teléfono"
                  autoComplete="off"
                  error={touched.phone && Boolean(errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="email"
                  label="Email"
                  autoComplete="off"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <Field
                  as={TextField}
                  fullWidth
                  margin="normal"
                  name="commissionRate"
                  label="Tasa de Comisión (%)"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  error={touched.commissionRate && Boolean(errors.commissionRate)}
                  helperText={touched.commissionRate && errors.commissionRate}
                />
                {selectedBranch && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.isActive}
                        onChange={(e) => setFieldValue('isActive', e.target.checked)}
                      />
                    }
                    label="Sucursal activa"
                    sx={{ mt: 1 }}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancelar</Button>
                <Button type="submit" variant="contained" color="primary">
                  Guardar
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default BranchManagement;
